import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Logo from "../shared/Logo";
import "./EmailVerificationExpired.css";
import appConstants from "../shared/config/constants.json";
import { verifyEmailApi } from "../../services/authService";

const EmailVerificationExpired = () => {
  const { MAYAMAYA_TEST_SUPPORT_URL } = appConstants;

  // State management for the expired page and success state
  // State management for verification API response
  const [state, setState] = useState("loading"); // loading, success, expired
  const [userEmail, setUserEmail] = useState("");
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const isExpired = state === "expired";

  const headerTitle =
    state === "loading"
      ? "Verifying Email"
      : isExpired
        ? "Verification Link Expired"
        : "Email Verified Successfully";

  const headerSubtitle =
    state === "loading"
      ? "Please wait while we verify your email address."
      : isExpired
        ? "This verification link is no longer valid or has already expired."
        : "Your email has been verified successfully. Redirecting you to signin.";

  const headerIconClass = isExpired
    ? "eve-icon-expired"
    : "eve-icon-success-header";

  // Simulate cooldown timer
  /*
  useEffect(() => {
    if (cooldownTimer && cooldownTimer > 0) {
      const interval = setInterval(() => {
        setCooldownTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [cooldownTimer]);
  */

  // Format timer display
  /*
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${secs}s`;
  };
  */

  const navigate = useNavigate();

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setState("expired");
        return;
      }

      try {
        const result = await verifyEmailApi(token);

        if (result?.code === 200 && result?.data?.verifiedEmail) {
          setUserEmail(result?.data?.email || "");
          setState("success");

          setTimeout(() => {
            navigate("/");
          }, 3000);
        } else {
          setState("expired");
        }
      } catch (error) {
        console.error("Email verification failed:", error);
        setState("expired");
      }
    };

    verifyEmail();
  }, [token, navigate]);


  const handleGoToProfile = () => {
    navigate("/");
  };

  // Simulate general error
  /*
  const handleSimulateError = () => {
    setState('error-general');
  };

  // Reset to initial state
  const handleReset = () => {
    setState('initial');
  };
  */

  return (
    <div className="eve-container">
      <div className="eve-top-bar">
        <Logo width={100} height={50} className="logo-svg" />
      </div>
      <div className="eve-content-wrapper">
        {/* Header Section */}
        <div
          className={`eve-header ${state === "success" ? "eve-header-success" : ""}`}
        >
          <div className={`eve-icon-wrapper ${headerIconClass}`}>
            {isExpired ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
                <path d="M9 2l-3 3m6 0l3-3" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M8.5 12.5l2.5 2.5 4.5-5" />
              </svg>
            )}
          </div>
          <h1 className="eve-title">{headerTitle}</h1>
          <p className="eve-subtitle">{headerSubtitle}</p>
        </div>

        {/* Main Content Section */}
        <div className="eve-main-content">
          {isExpired ? (
            <>
              {/* State: Initial */}
                            <div className="eve-state-wrapper eve-state-initial">
                <div className="eve-message-box eve-message-info">
                  <div className="eve-message-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                  <div className="eve-message-content">
                    <p className="eve-message-text">
                      This verification link is no longer valid. Please request a
                      new verification email from your profile page.
                    </p>
                  </div>
                </div>

                <p className="eve-secondary-text">
                  Request new verification email from profile page.
                </p>

                {/* Resend button removed as per new flow.
                <button
                  className="eve-primary-button"
                  onClick={handleSendVerificationEmail}
                >
                  Send Verification Email Again
                </button>
                */}
              </div>

              {/* State: Success */}
              {state === "success" && (
                <div className="eve-state-wrapper eve-state-success">
                  <div className="eve-message-box eve-message-success">
                    <div className="eve-message-icon eve-icon-success">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <div className="eve-message-content">
                      <h3 className="eve-success-title">
                        Email Sent Successfully
                      </h3>
                      <p className="eve-message-text">
                        A new verification link has been sent to{" "}
                        <strong>{userEmail}</strong>. Please check your inbox
                        and click the link within 24 hours.
                      </p>
                    </div>
                  </div>

                  <div className="eve-success-checklist">
                    <div className="eve-checklist-item">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>Check your inbox for the verification email</span>
                    </div>
                    <div className="eve-checklist-item">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>Click the verification link within 24 hours</span>
                    </div>
                    <div className="eve-checklist-item">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>If not found, check your spam/junk folder</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="eve-state-wrapper eve-state-success">
              <div className="eve-message-box eve-message-success">
                <div className="eve-message-icon eve-icon-success">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div className="eve-message-content">
                  <h3 className="eve-success-title">
                    Email Verified Successfully
                  </h3>
                  <p className="eve-message-text">
                    {userEmail ? (
                      <>
                        Your email <strong>{userEmail}</strong> has been verified successfully.
                      </>
                    ) : (
                      <>Your email has been verified successfully.</>
                    )}{" "}
                    Redirecting you to signin.
                  </p>
                </div>
              </div>

              <div className="eve-success-checklist">
                <div className="eve-checklist-item">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>Your email verification is complete.</span>
                </div>
                <div className="eve-checklist-item">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>You can now continue to your profile and portal.</span>
                </div>
                <div className="eve-checklist-item">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>If you need help, contact support for assistance.</span>
                </div>
              </div>
            </div>
          )}

          {/* State: Loading */}
          {/*{state === 'loading' && (
            <div className="eve-state-wrapper eve-state-loading">
              <div className="eve-spinner-container">
                <div className="eve-spinner"></div>
              </div>
              <p className="eve-loading-text">Sending verification email...</p>
            </div>
          )}*/}

          {/* State: Rate Limit Exceeded */}
          {/*{state === 'error-ratelimit' && (
            <div className="eve-state-wrapper eve-state-error">
              <div className="eve-message-box eve-message-error">
                <div className="eve-message-icon eve-icon-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                </div>
                <div className="eve-message-content">
                  <h3 className="eve-error-title">Too Many Requests</h3>
                  <p className="eve-message-text">
                    You have reached the maximum number of verification email requests (3 per 24 hours). Please try again later.
                  </p>
                </div>
              </div>

              <div className="eve-cooldown-box">
                <p className="eve-cooldown-label">You can request a new email in:</p>
                <p className="eve-cooldown-timer">{cooldownTimer && formatTime(cooldownTimer)}</p>
                <p className="eve-cooldown-hint">
                  In the meantime, check your inbox for a previous verification email that may still be valid.
                </p>
              </div>

              <button className="eve-secondary-button" onClick={handleReset}>
                Go Back
              </button>

              <p className="eve-support-text">
                Need help? <a   href={MAYAMAYA_TEST_SUPPORT_URL} className="eve-link">Contact support</a>
              </p>
            </div>
          )}*/}

          {/* State: General Error */}
          {/*{state === 'error-general' && (
            <div className="eve-state-wrapper eve-state-error">
              <div className="eve-message-box eve-message-error">
                <div className="eve-message-icon eve-icon-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <div className="eve-message-content">
                  <h3 className="eve-error-title">Something Went Wrong</h3>
                  <p className="eve-message-text">
                    We encountered an error while sending the verification email. Please try again or contact support if the problem persists.
                  </p>
                </div>
              </div>

              <div className="eve-error-actions">
                <button className="eve-primary-button" onClick={handleReset}>
                  Try Again
                </button>
                <button className="eve-secondary-button" onClick={handleReset}>
                  Go Back
                </button>
              </div>

              <p className="eve-support-text">
                Still having issues? <a href="#support" className="eve-link">Contact support</a>
              </p>
            </div>
          )}*/}
        </div>

        {/* Footer Navigation */}
        <div className="eve-footer-nav">
          <button
            type="button"
            onClick={handleGoToProfile}
            className="eve-nav-link"
            style={{ border: "none" }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Go to Profile
          </button>
        </div>

        {/* Debug Controls - For demonstration purposes */}
        {/*{process.env.NODE_ENV === 'development' && (
          <div className="eve-debug-controls">
            <p className="eve-debug-label">Debug Controls:</p>
            <button onClick={() => setState('initial')} className="eve-debug-button">
              State: Initial
            </button>
            <button onClick={() => setState('loading')} className="eve-debug-button">
              State: Loading
            </button>
            <button onClick={() => setState('success')} className="eve-debug-button">
              State: Success
            </button>
            <button onClick={() => setState('error-ratelimit')} className="eve-debug-button">
              State: Rate Limit
            </button>
            <button onClick={handleSimulateError} className="eve-debug-button">
              State: General Error
            </button>
            <button onClick={() => setAttemptCount(1)} className="eve-debug-button">
              Reset Attempts
            </button>
          </div>
        )}*/}
      </div>
    </div>
  );
};

export default EmailVerificationExpired;
